import { inject, Injectable } from '@angular/core'
import { HearingSectionPeopleAnnotation } from '@ftr/annotations-contracts'
import { Uuid } from '@ftr/contracts/type/shared'
import { RemoteData, tapFailure, unwrapData } from '@ftr/foundation'
import { Action, createSelector, Selector, State, StateContext, StateOperator } from '@ngxs/store'
import { patch } from '@ngxs/store/operators'
import { memoize } from 'lodash-es'
import { firstValueFrom, Observable } from 'rxjs'
import { HearingSectionPeopleAnnotationsService } from '../../services'
import {
  CreateHearingSectionPeopleAnnotationsCommand,
  DeleteHearingSectionPeopleAnnotationsCommand,
  FetchPeopleAnnotationsCommand,
  InitialisePeopleAnnotationsCommand,
  UpdateHearingSectionPeopleAnnotationsCommand,
} from './people-annotations.commands'
import { PeopleAnnotationsInstanceStateModel, PeopleAnnotationsStateModel } from './people-annotations.model'

export const getDefaultPeopleAnnotationsState: () => PeopleAnnotationsStateModel = () => ({
  instances: {},
})

@State({
  name: 'peopleAnnotations',
  defaults: getDefaultPeopleAnnotationsState(),
})
@Injectable()
export class PeopleAnnotationsState {
  private readonly hearingSectionPeopleAnnotationsService = inject(HearingSectionPeopleAnnotationsService)

  @Selector()
  static allInstanceStates(state: PeopleAnnotationsStateModel): Record<Uuid, PeopleAnnotationsInstanceStateModel> {
    return state?.instances ?? {}
  }

  static readonly peopleAnnotations = memoize(
    (
      courtSystemId: string,
    ): ((
      sourceStates: ReturnType<typeof PeopleAnnotationsState.allInstanceStates>,
    ) => PeopleAnnotationsInstanceStateModel) =>
      createSelector([PeopleAnnotationsState.allInstanceStates], sourceStates => {
        return sourceStates[courtSystemId] ?? {}
      }),
    courtSystemId => courtSystemId,
  )

  static readonly hearingSectionPeopleAnnotations = memoize(
    (
      courtSystemId: string,
      hearingSectionId: string,
    ): ((
      sourceStates: ReturnType<typeof PeopleAnnotationsState.allInstanceStates>,
    ) => HearingSectionPeopleAnnotation[]) =>
      createSelector([PeopleAnnotationsState.allInstanceStates], sourceStates => {
        const { hearingSectionAnnotations = [] } = sourceStates[courtSystemId] ?? {}

        return hearingSectionAnnotations.filter(annotation => annotation.hearingSectionId === hearingSectionId)
      }),
    (courtSystemId, hearingSectionId) => `${courtSystemId}-${hearingSectionId}`,
  )

  static readonly status = memoize(
    (
      courtSystemId: string,
    ): ((sourceStates: ReturnType<typeof PeopleAnnotationsState.allInstanceStates>) => RemoteData<null>) =>
      createSelector([PeopleAnnotationsState.allInstanceStates], sourceStates => {
        return sourceStates[courtSystemId]?.status ?? RemoteData.notAsked()
      }),
    courtSystemId => courtSystemId,
  )

  @Action(InitialisePeopleAnnotationsCommand)
  private initialisePeopleAnnotationsCommand(
    { setState, getState, dispatch }: StateContext<PeopleAnnotationsStateModel>,
    { courtSystemId, recordingId, audioSegmentId }: InitialisePeopleAnnotationsCommand,
  ): Observable<void> | undefined {
    const { status, recordingIds = [], hearingSectionAnnotations = [] } = getState().instances[courtSystemId] ?? {}

    if ((recordingIds.includes(recordingId) && status?.isCompleted()) || status?.isLoading()) {
      return
    }

    setState(
      makeInstancePatch(courtSystemId, {
        hearingSectionAnnotations,
        recordingIds,
        status: RemoteData.notAsked(),
      }),
    )

    return dispatch(new FetchPeopleAnnotationsCommand(courtSystemId, recordingId, { audioSegmentId }))
  }

  @Action(FetchPeopleAnnotationsCommand)
  private async fetchPeopleAnnotations(
    { getState, setState }: StateContext<PeopleAnnotationsStateModel>,
    { courtSystemId, recordingId, audioSegmentId, force }: FetchPeopleAnnotationsCommand,
  ): Promise<void> {
    const { status, recordingIds = [], hearingSectionAnnotations = [] } = getState().instances[courtSystemId] ?? {}

    if (!force && status.isLoading() && recordingIds.includes(recordingId)) {
      return
    }

    setState(
      makeInstancePatch(courtSystemId, {
        // Reset recordingIds so the de-dupe check is bypassed, but keep annotations visible during loading to avoid flash
        ...(force ? { recordingIds: [] } : {}),
        status: RemoteData.loading(),
      }),
    )

    try {
      const result = await firstValueFrom(
        this.hearingSectionPeopleAnnotationsService
          .listHearingSectionPeopleAnnotations({
            courtSystemId,
            recordingId,
            audioSegmentId,
          })
          .pipe(
            tapFailure(err => {
              throw err
            }),
            unwrapData(),
          ),
      )

      const existingAnnotations = force ? [] : hearingSectionAnnotations
      const existingRecordingIds = force ? [] : recordingIds

      setState(
        makeInstancePatch(courtSystemId, {
          hearingSectionAnnotations: existingAnnotations.concat(result),
          status: RemoteData.success(null),
          recordingIds: existingRecordingIds.concat(recordingId),
        }),
      )
    } catch (err) {
      setState(
        makeInstancePatch(courtSystemId, {
          hearingSectionAnnotations: [],
          status: RemoteData.failure(new Error('Failed to fetch people annotation', { cause: err })),
        }),
      )
    }
  }

  @Action(CreateHearingSectionPeopleAnnotationsCommand)
  private async createHearingSectionPeopleAnnotations(
    { setState, getState }: StateContext<PeopleAnnotationsStateModel>,
    { request }: CreateHearingSectionPeopleAnnotationsCommand,
  ): Promise<void> {
    const { courtSystemId } = request
    const { hearingSectionAnnotations = [] } = getState().instances[courtSystemId] ?? {}

    setState(
      makeInstancePatch(request.courtSystemId, {
        status: RemoteData.loading(),
      }),
    )

    try {
      const result = await this.hearingSectionPeopleAnnotationsService.createHearingSectionPeopleAnnotation(request)

      if (result) {
        setState(
          makeInstancePatch(courtSystemId, {
            hearingSectionAnnotations: [...hearingSectionAnnotations, result],
            status: RemoteData.success(null),
          }),
        )
      }
    } catch (err) {
      setState(
        makeInstancePatch(courtSystemId, {
          status: RemoteData.failure(new Error('Failed to add people annotation', { cause: err })),
        }),
      )
    }
  }

  @Action(UpdateHearingSectionPeopleAnnotationsCommand)
  private async updateHearingSectionPeopleAnnotations(
    { setState, getState }: StateContext<PeopleAnnotationsStateModel>,
    { request }: UpdateHearingSectionPeopleAnnotationsCommand,
  ): Promise<void> {
    const { courtSystemId } = request
    const { hearingSectionAnnotations = [] } = getState().instances[courtSystemId] ?? {}

    setState(
      makeInstancePatch(courtSystemId, {
        status: RemoteData.loading(),
      }),
    )

    try {
      const result = await firstValueFrom(
        this.hearingSectionPeopleAnnotationsService.updateHearingSectionPeopleAnnotation(request).pipe(
          tapFailure(err => {
            throw err
          }),
          unwrapData(),
        ),
      )

      if (result) {
        const filteredAnnotations = hearingSectionAnnotations.filter(a => a.id !== result.id)
        setState(
          makeInstancePatch(courtSystemId, {
            hearingSectionAnnotations: [...filteredAnnotations, result],
            status: RemoteData.success(null),
          }),
        )
      }
    } catch (err) {
      setState(
        makeInstancePatch(courtSystemId, {
          status: RemoteData.failure(new Error('Failed to update people annotation', { cause: err })),
        }),
      )
    }
  }

  @Action(DeleteHearingSectionPeopleAnnotationsCommand)
  private async deleteHearingSectionPeopleAnnotations(
    { setState, getState }: StateContext<PeopleAnnotationsStateModel>,
    { request }: DeleteHearingSectionPeopleAnnotationsCommand,
  ): Promise<void> {
    const { courtSystemId, id } = request
    const { hearingSectionAnnotations = [] } = getState().instances[courtSystemId] ?? {}

    setState(
      makeInstancePatch(courtSystemId, {
        status: RemoteData.loading(),
      }),
    )

    try {
      await firstValueFrom(
        this.hearingSectionPeopleAnnotationsService.deleteHearingSectionPeopleAnnotation(request).pipe(
          tapFailure(err => {
            throw err
          }),
          unwrapData(),
        ),
      )

      setState(
        makeInstancePatch(courtSystemId, {
          hearingSectionAnnotations: hearingSectionAnnotations.filter(a => a.id !== id),
          status: RemoteData.success(null),
        }),
      )
    } catch (err) {
      setState(
        makeInstancePatch(courtSystemId, {
          status: RemoteData.failure(new Error('Failed to delete people annotation', { cause: err })),
        }),
      )
    }
  }
}

function makeInstancePatch(
  instanceId: string,
  instancePartial: Partial<PeopleAnnotationsInstanceStateModel>,
): StateOperator<PeopleAnnotationsStateModel> {
  return patch({
    instances: patch({
      [instanceId]: patch(instancePartial),
    }),
  })
}
